import Handlebars from "utilities/vendor/handlebars/4.7.7/js/handlebars";
import hbs from "utilities/vendor/handlebars/4.7.7/js/handlebars";
/* global Handlebars util */
/* eslint-disable no-console */

/**
 * An object literal wrapper for the helpers.
 * @constant {object}
 */
const helpers = {
  /**
   * Abbreviation helper.
   * @param {object} a - Data for the `abbr` tag.
   * @returns {string} The generated markup.
   */
  abbr(a) {
    const abbr = a;
    if (!abbr) {
      return '';
    }

    const { value } = abbr;
    delete abbr.value;
    let attr = '';

    const attrTemplate = (n, v) => `${n}="${v}"`;
    const abbrTemplate = (attributes, text) => `<abbr ${attributes}>${text}</abbr>`;

    Object.keys(abbr).forEach((key) => {
      attr += attrTemplate(key, abbr[key]);
    });

    return abbrTemplate(attr, value);
  },

  /**
   * ADA block helper. Adds `ada-hidden` class.
   * @param {object} options - Content for the hidden `span`.
   * @returns {object} The generated markup wrapped to indicate it should never be escaped.
   */
  ada(options) {
    return new Handlebars.SafeString(
      `<span class="ada-hidden" data-version="2.0.0">${options.fn(this)}</span>`,
    );
  },

  /**
   * Debug helper. Console logs out debug message.
   * @param {string} logName - Log name label.
   * @returns {object} The generated markup wrapped to indicate it should never be escaped.
   */
  debug(logName) {
    const label = (typeof logName !== 'undefined') ? `Sparta Debug: ${logName}` : '';

    const output = `<script>
        console.group('${label}');
        console.group(${JSON.stringify(this)});
        console.groupEnd();
        console.groupEnd();
      </script>`;

    return new Handlebars.SafeString(output);
  },

  /**
   * Debug print helper. Prints out debug message on the page.
   * @param {string} logName - Log name label.
   * @returns {object} The generated markup wrapped to indicate it should never be escaped.
   */
  debug_print(logName) {
    const label = (typeof logName !== 'undefined') ? `Sparta Debug: ${logName}` : '';

    const debugStyles = {};
    debugStyles.wrapper = 'position:relative; border:5px solid #333; padding:18px 10px 10px; margin: 5px 0;';
    debugStyles.header = 'position: absolute; top: 0; left: 0; width: 100%; height: 18px; font-size: 11px; background-color: #333; color: #fff; padding: 0; margin: 0;';

    const output = `<div class="spa-debug" data-debug="${label}" style="${debugStyles.wrapper}">
        <pre><h3 style="${debugStyles.header}">${label}</h3>
        ${JSON.stringify(this, 0, 2)}
        </pre>
      </div>`;

    return new Handlebars.SafeString(output);
  },

  /**
   * @param {object} attributes - holds the values to get added to the element on compile.
   * @returns {string} output - this is the string that gets built from each of the keys in the JCR.
   */
  elementAttributes(attributes) {
    if (!attributes) return;
    let output = [];
    const attrs = Object.entries(attributes)
    attrs.forEach(el => {
      let elementAttribute = el[0];
      let elementAttributeData = el[1];
      // Check to see if attribute is an Object data type.
      if(typeof(elementAttributeData) === 'object'){
        // If the value is an Object, check to see if the value is an Array.
        if (Array.isArray(elementAttributeData)){
          // If the value is an Array, assign all elements of the array to the attribute.
          // This is intended to be for the style attribute for inline styles, You will need each index to end with a semicolon (';'), and should be separated by a space(' ');
          let tempArray = [];
          elementAttributeData.forEach(el => {
            let index = el.trim();
            if(index.substring(index.length - 1) !== `;`) {
              index += `;`;
            }
            tempArray.push(index);
          });
          output.push(`${elementAttribute.toLowerCase()}="${tempArray.join(` `)}"`);
        } else {
          // If the value is an Object, iterate over each key prefixing with the object name assigning the value of the key.
          let prefix = elementAttribute.toLowerCase();
          Object.keys(elementAttributeData).forEach(el =>
            (output.push(`${prefix}-${el}="${attributes[elementAttribute][el]}"`)));
          }
      // If the value is not an object, check for a boolean. If the boolean is true then add the attribute only to the element.
      } else if(typeof(elementAttributeData) === 'boolean') {
        if (elementAttributeData === true) {
          output.push(`${elementAttribute}`);
        }
      // Failing the other checks, just assign the attribute the key with the value.
      } else {
        output.push(`${elementAttribute}="${elementAttributeData}"`);
      }
    })
    // Creates a Handlebars Safe String to compile in to the HTML.
    return new Handlebars.SafeString(` ${output.join(" ")}`);
  },

  /**
   * Link helper.
   * @param {object} linkData - Data for the `a` tag.
   * @returns {object} The generated markup wrapped to indicate it should never be escaped.
   */
  link(linkData) {
    // Return empty string if a link object is not provided.
    if (!linkData) {
      return '';
    }
    const link = linkData;

    link.CLASS = link.CLASS || '';

    let windowOptions;
    const layerClasses = {
      layer: 'spa-ui-layer-link spa-fn',
      popup: 'spa-fn spa-boa-window',
    };
    let showAdaLayer = false;

    // Handling buttons.
    if (link.button) {
      link.CLASS += ' spa-btn';

      const { button } = link;

      const buttonsConfig = {
        sizes: {
          default: 'spa-btn--small',
          small: 'spa-btn--small',
          medium: 'spa-btn--medium',
          large: 'spa-btn--large',
          engagement: 'spa-btn--engagement',
        },
        skins: {
          default: 'spa-btn--primary',
          primary: 'spa-btn--primary',
          secondary: 'spa-btn--secondary',
          reverse: 'spa-btn--reverse',
          disabled: 'spa-btn--disabled',
        },
        expand: {
          small: 'spa-btn--expand-on-small',
          medium: 'spa-btn--expand-on-medium',
        },
      };

      /* serverSideOnly */
      // Checking for configuration mismatches.
      if ((button.width || button.height) && button.size) {
        console.log('You may not set the button size with width and/or height. Choose one.');
      }
      /* serverSideOnly */

      // Handle button skin.
      if (button.skin) {
        link.CLASS += ` ${buttonsConfig.skins[button.skin]}`;
      }

      // Handle expand.
      if (button.expand) {
        const buttonExpandKeys = Object.keys(buttonsConfig.expand);

        /* serverSideOnly */
        if (buttonExpandKeys.indexOf(button.expand) === -1) {
          console.log(`Button expand property may only be set as ${buttonExpandKeys.join(', ')}.`);
        }
        /* serverSideOnly */

        link.CLASS += ` ${buttonsConfig.expand[button.expand]}`;
      }

      // Handle block.
      if (button.block) {
        link.CLASS += ' spa-btn--block';
      }

      // Handle button sizes, except for large since it is default.
      if (button.size) {
        const buttonSizeKeys = Object.keys(buttonsConfig.sizes);

        /* serverSideOnly */
        if (!buttonsConfig.sizes[button.size]) {
          console.log(`Button size property may only be set as ${buttonSizeKeys.join(', ')}.`);
        }
        /* serverSideOnly */

        link.CLASS += ` ${buttonsConfig.sizes[button.size]}`;
      } else {
        link.CLASS += ` ${buttonsConfig.sizes.default}`;
      }
    }

    // Handling guillemet.
    if (link.guillemet) {
      const { guillemet } = link;

      link.CLASS += ' spa-guillemet';

      if (guillemet === 'left') {
        link.CLASS += ' spa-guillemet--left';
      }
    }

    // Check if `actionType` exists.
    if (link.actionType) {
      // If options are defined...
      if (link.actionType.options) {
        windowOptions = link.actionType.options;
        link.OPTIONS = JSON.stringify(link.actionType.options);
      }

      // Handle info, modal, and task layers.
      if (link.actionType.type === 'layer') {
        showAdaLayer = true;
        link.REL = link.actionType.options.rel;
        link.CLASS += ` ${layerClasses[link.actionType.type]}`;

        if (link.actionType.options && link.actionType.options.interstitalType) {
          // Handle for interstitials.
          link.CLASS += ' spa-ui-layer-interstitial';
        } else if (link.actionType.options && link.actionType.options.layerType) {
          // Tooltip layer.
          link.CLASS += ' spa-ui-layer-tooltip-link';
        }
      }

      // Handle popups.
      if (link.actionType.type === 'popup') {
        /* serverSideOnly */
        // Checking whether the popup window dimensions and size are set.
        if ((windowOptions.width || windowOptions.height) && windowOptions.size) {
          console.log(
            util.format('You may not set the popup size with width and/or height. Choose one.'),
          );
        }
        /* serverSideOnly */

        link.CLASS += ` ${layerClasses[link.actionType.type]}`;
      }
    }

    // Trim leading space on class.
    if (link.CLASS.length) {
      link.CLASS = link.CLASS.replace(/^\s+|\s+$/g, '');
    }

    // eslint-disable-next-line no-script-url
    const linkUrl = link.URL || 'javascript:void(0);';
    const output = `<a
        id="${link.ID || ''}"
        href="${linkUrl}"
        ${link.DATA_URL ? `data-href="${link.DATA_URL}"` : ''}
        ${link.CLASS ? `class="${link.CLASS}"` : ''}
        ${link.REL ? `rel="${link.REL}"` : ''}
        ${link.OPTIONS ? `data-options="${Handlebars.Utils.escapeExpression(link.OPTIONS)}"` : ''}
        ${link.TARGET ? `target="${link.TARGET}"` : ''}
        ${link.ROLE ? `role="${link.ROLE}"` : ''}
        ${link.bactmln ? `data-bactmln="${link.bactmln}"` : ''}
        ${link.tabIndex ? `tabindex="${link.tabIndex}"` : ''}
        ${link.ARIA_LABEL ? `aria-label="${ARIA_LABEL}"` : ''}
      >
        ${link.content}
        ${showAdaLayer ? '<span class="spa-ada-text ada-hidden">&nbsp;layer</span>' : ''}
      </a>`;

    return new Handlebars.SafeString(output);
  },

  /**
   * Layer Content helper.
   * @param {object} data - Data to be included in the `data-options` attribute.
   * @param {object} options - Layer content to be wrapped.
   * @returns {object} The generated markup wrapped to indicate it should never be escaped.
   */
  layerContent(data, options) {
    const escapedOptions = Handlebars.Utils.escapeExpression(JSON.stringify(data.options));

    // set title tag type
    let tagType = data.options.type === 'modal'
    ? 'h1'
    : 'h3'

    let showTitle = '';
    if (data.title) {
      showTitle = `<${tagType} id="${data.id}_title" class="spa-ui-layer-title" data-font="cnx-regular">${data.title}</${tagType}>`;
    }
    const output = `<div id="${data.id}" class="hide" data-options="${escapedOptions}">
        ${showTitle}
        <div class="content">${options.fn(this)}</div>
    </div>`;

    return new Handlebars.SafeString(output);
  },

  /**
   * Sprite Rules helper.
   * @param {object} o - Data for the sprite's CSS.
   * @returns {string} The generated CSS.
   */
  spriteRules(o) {
    let output = '';
    const writeRule = (exp) => {
      let css = '';

      switch (exp.experience) {
        case 'hires':
          css += 'html.mq-hires ';
          break;
        case 'fallback':
          css += 'html.lt-ie9 ';
          break;
        default: break;
      }
      if (exp.context !== 'default') {
        css += `.${exp.context} `;
      }

      css += `.sprite .spr {\n\tbackground-image: url("${exp.image}");\n`;
      if (exp.width && exp.height) {
        css += `\tbackground-size: ${exp.width} ${exp.height};\n`;
      }
      css += '}\n\n';

      return css;
    };

    const oMod = o;
    Object.keys(o).forEach((context) => {
      Object.keys(o[context]).forEach((experience) => {
        oMod[context][experience].context = context;
        oMod[context][experience].experience = experience;
        output += writeRule(oMod[context][experience]);
      });
    });

    return output;
  },

  /**
   * Write Background Image helper.
   * @returns {string} The generated markup.
   */
  writeBackgroundImage(...args) {
    const breakpoints = [];
    let output = '';

    const writeBreakpoint = (breakpointArgs, breakpoint) => {
      const selector = breakpointArgs[0];
      let lowres = [];
      let hires = [];
      let position = [];
      let size = [];
      let mq = '';
      let css = '';

      const addAttribute = (item, marker, list) => {
        if (typeof item === 'undefined' || typeof item[marker] === 'undefined') {
          return list;
        }

        switch (typeof item[marker]) {
          case 'string':
            list.push(item[marker]);
            break;

          case 'object': {
            const tmp = [];
            Object.keys(item[marker]).forEach((key) => {
              if (key === 'width' || key === 'height') {
                addAttribute(item[marker], key, tmp);
              }
            });
            list.push(tmp.join(' '));
            break;
          }

          default: break;
        }

        return list;
      };

      const writeRule = (attribute, ruleSelector, values, filter) => {
        let value = values.join(', ');
        if (attribute === 'background-image') {
          value = `url("${values.join('"), url("')}")`;
        }

        return (values.length > 0)
          ? `${filter ? `${filter} ` : ''}${ruleSelector} { ${attribute}: ${value}; }\n`
          : '';
      };

      for (let i = 1; i < breakpointArgs.length - 1; i += 1) {
        try {
          const b = breakpointArgs[i][breakpoint];

          lowres = addAttribute(b, 'lowres', lowres);
          hires = addAttribute(b, 'hires', hires);
          if (lowres.length > hires.length) {
            hires.push(lowres[lowres.length - 1]);
          }

          size = addAttribute(b, 'size', size);
          position = addAttribute(b, 'position', position);
        } catch (err) {
          // Fail silently.
        }
      }

      mq = (breakpoint === 'default') ? '' : `html.mq-${breakpoint}`;

      css += writeRule('background-image', selector, lowres, mq);
      css += writeRule(
        'background-image',
        selector,
        hires,
        `html.mq-hires${mq.replace('html', '')}`,
      );
      css += writeRule('background-size', selector, size, mq);
      css += writeRule('background-position', selector, position, mq);

      return css;
    };

    // Function should gather all the various breakpoint sizes that apply to all arguments, then
    // run CSS rule-builders for each breakpoint.
    for (let i = 1; i < args.length - 1; i += 1) {
      Object.keys(args[i]).forEach((breakpoint) => {
        if (breakpoints.indexOf(breakpoint) === -1) {
          breakpoints.push(breakpoint);
        }
      });
    }

    // Loop through the collective breakpoints to write the rules.
    for (let b = 0; b < breakpoints.length; b += 1) {
      output += writeBreakpoint(args, breakpoints[b]);
    }

    return new Handlebars.SafeString(output);
  },

  /**
   * Write Dynamic ID helper.
   * @param {string} type - Determines which data will be included in the generated ID.
   * @param {string} base - A prefix for the ID, not including the hyphen which will be added.
   * @param {string} output - If set to "bare", only the ID itself will be returned.
   * @returns {string} The generated `id` attribute markup.
   */
  writeDynamicId(type, base, output) {
    let id = '';
    const content = [];
    const badCharacters = ['&amp;', '[^A-Za-z0-9]'];
    const cleanCharacters = (str) => {
      const replaceThese = new RegExp(badCharacters.join('|'), 'g');
      let strMod = str.replace(replaceThese, '-');

      strMod = strMod.split('-').filter(n => (n !== '')).join('-');
      strMod = strMod.replace(/^-|-$/g, '');
      return strMod;
    };
    const writeAttribute = idValue => ` id="${idValue}"`;

    const typeMod = (this.id && base) ? 'id' : type;
    switch (typeMod) {
      case 'id':
        content.push(this.id);
        break;

      case 'simple':
        if (this.text) {
          content.push(this.text);
        }
        break;

      case 'faq':
        if (this.question) {
          content.push(this.question);
        }
        break;

      case 'guide':
        if (this.heading) {
          content.push(this.heading);
        }
        if (this.subHeading) {
          content.push(this.subHeading);
        }
        break;

      case 'map':
        if (this.meta.type) {
          content.push(this.meta.type);
        }
        if (this.location.street1) {
          content.push(this.location.street1);
        }
        if (this.location.street2) {
          content.push(this.location.street2);
        }
        if (this.location.postal) {
          content.push(this.location.postal);
        }
        break;

      default: break;
    }

    if (content.length > 0) {
      id = content.join('-').toLowerCase();
      id = cleanCharacters(id);
      if (base && base.length > 0 && id.indexOf(base) !== 0) {
        id = `${base}-${id}`;
      }
      if (output !== 'bare') {
        id = writeAttribute(id);
      }
    }

    return id;
  },

  /**
   * Adds a given amount to a given pixel value.
   * @param {string} value - CSS-friendly pixel value to add to.
   * @param {number} change - Number of pixels to be added.
   * @returns {string} The new CSS-friendly pixel value.
   */
  spriteAdd(value, change) {
    if (typeof value !== 'undefined') {
      return `${parseInt(value.replace('px', ''), 10) + change}px`;
    }
    return value;
  },

  /**
   * Subtracts a given amount from a given pixel value.
   * @param {string} value - CSS-friendly pixel value to subtract from.
   * @param {number} change - Number of pixels to be subtracted.
   * @returns {string} The new CSS-friendly pixel value.
   */
  spriteSubtract(value, change) {
    if (typeof value !== 'undefined') {
      return `${parseInt(value.replace('px', ''), 10) - change}px`;
    }
    return value;
  },

  /**
   * Increments a number and returns it.
   * @param {*} value - Number to increment.
   * @returns {number} The new incremented value.
   */
  incrementValue(value) {
    if (typeof value !== 'undefined') {
      return parseInt(value, 10) + 1;
    }
    return 0;
  },

  /**
   * Helper to create a footnote reference.
   * @param {string} target - The ID of the reference.
   * @returns {object} The generated markup wrapped to indicate it should never be escaped.
   */
  footnote(target) {
    const output = `<span class="spa-footnote-ref" data-footnote-ref="${target}"></span>`;
    return new Handlebars.SafeString(output);
  },
};

/**
 * This class is used to register the helpers with Handlebars.
 */
class helpersSparta {
  /**
   * Registers the helpers with Handlebars.
   * @param {object} Handlebars Reference to Handlebars for helper registration.
   * @returns {void}
   */
  static register(Handlebars) {
    Object.keys(helpers).forEach((key) => {
      Handlebars.registerHelper(key, helpers[key]);
    });
  }
}

module.exports = helpersSparta;
module.exports.helpers = helpers;
